import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHistories } from '../services/historyService';
import { getEvents, deleteEvent } from '../services/eventsService';
import { getDisplayErrorMessage } from '../services/apiError';
import ErrorMessage from './ErrorMessage';

const HistoryList = () => {
  const [historyList, setHistoryList] = useState([]);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [expandedHistoryEvents, setExpandedHistoryEvents] = useState([]);
  const [isExpandedHistoryOwner, setIsExpandedHistoryOwner] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistoryList = async () => {
      try {
        const data = await getHistories();
        setHistoryList(data);
      } catch (err) {
        setError(getDisplayErrorMessage(err));
      }
    };
    fetchHistoryList();
  }, []);

  useEffect(() => {
    if (!expandedHistory) {
      setIsExpandedHistoryOwner(false);
      return;
    }

    const history = historyList.find(h => h.id === expandedHistory);
    if (history) {
      setIsExpandedHistoryOwner(history.isEditable || false);
    } else {
      setIsExpandedHistoryOwner(false);
    }
  }, [expandedHistory, historyList]);

  useEffect(() => {
    const fetchHistoryEvents = async () => {
      if (expandedHistory) {
        try {
          const data = await getEvents(expandedHistory);
          setExpandedHistoryEvents(data);
        } catch (err) {
          setError(getDisplayErrorMessage(err));
          setExpandedHistoryEvents([]);
        }
      } else {
        setExpandedHistoryEvents([]);
      }
    };
    fetchHistoryEvents();
  }, [expandedHistory]);

  const handleToggleDetails = (history) => {
    setExpandedHistory(prevId => prevId === history.id ? null : history.id);
  }

  const handleEditHistoryClick = (history) => {
    navigate(`/editHistory/${history.id}`, { state: { history } });
  };

  const handleAddEventClick = (history) => {
    navigate(`/createEvent/${history.id}`);
  };

  const handleDeleteEventClick = async (event) => {
    try {
      await deleteEvent(event.id);
      setExpandedHistoryEvents(prevEvents => prevEvents.filter(e => e.id !== event.id));
    } catch (err) {
      setError(getDisplayErrorMessage(err));
    }
  };

  const publicHistoryList = historyList.filter((history) => history.isPublic);
  const privateHistoryList = historyList.filter((history) => !history.isPublic);

  return (
    <div className="p-4 relative">
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      <h2 className="text-2xl mb-6 text-center font-bold text-gray-800">History list</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4 text-blue-600 border-b pb-2">Public Histories</h3>
          <div className="space-y-4">
            {publicHistoryList.length === 0 && <p className="text-gray-400">No public histories.</p>}

            {publicHistoryList.map((history) => (
              <div
                className="border border-blue-200 bg-blue-50 rounded-lg p-3"
                key={history.id}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-blue-900">{history.name}</h3>
                  <button
                    onClick={() => handleToggleDetails(history)}
                    className="bg-white text-2xl w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                    </svg>
                  </button>
                </div>
                {expandedHistory === history.id && (
                  <div className="mt-4 p-4 bg-blue-100 rounded-lg text-sm">
                    {isExpandedHistoryOwner && (
                      <div className="flex gap-2 mb-4">
                        <button
                        onClick={() => handleEditHistoryClick(history)}
                        className="bg-white w-8 h-8 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-700">
                          <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
                          <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
                        </svg>
                      </button>
                        <button 
                        onClick={() => handleAddEventClick(history)}
                        className="bg-white text-2xl w-8 h-8 pb-1 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                      >
                        +
                      </button>
                      </div>
                    )}
                    {expandedHistoryEvents.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        <h4 className="font-semibold">Events:</h4>
                        {expandedHistoryEvents.map((event) => (
                          <div
                            key={event.id}
                            className="border border-gray-200 bg-gray-50 rounded-lg p-3 flex justify-between items-center relative"
                          >
                            <span>{event.name}</span>
                            {isExpandedHistoryOwner && (
                              <button
                                onClick={() => handleDeleteEventClick(event)}
                                className="bg-white w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:bg-red-100 hover:border-2 hover:border-red-500"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        No events.
                      </div>
                      )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-xl font-bold mb-4 text-gray-600 border-b pb-2">Private Histories</h3>
          <div className="space-y-4">
            {privateHistoryList.length === 0 && <p className="text-gray-400">No private histories.</p>}
            
            {privateHistoryList.map((history) => (
              <div
                className="border border-gray-200 bg-gray-50 rounded-lg p-3"
                key={history.id}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold text-gray-900">{history.name}</h3>
                  <button
                    onClick={() => handleToggleDetails(history)}
                    className="bg-white text-2xl w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
                    </svg>
                  </button>
                </div>
                {expandedHistory === history.id && (
                  <div className="mt-4 p-4 bg-gray-100 rounded-lg text-sm">
                    {isExpandedHistoryOwner && (
                      <div className="flex gap-2 mb-4">
                        <button
                        onClick={() => handleEditHistoryClick(history)}
                        className="bg-white w-8 h-8 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-gray-700">
                          <path d="M5.433 13.917l1.262-3.155A4 4 0 017.58 9.42l6.92-6.918a2.121 2.121 0 013 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 01-.65-.65z" />
                          <path d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0010 3H4.75A2.75 2.75 0 002 5.75v9.5A2.75 2.75 0 004.75 18h9.5A2.75 2.75 0 0017 15.25V10a.75.75 0 00-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5z" />
                        </svg>
                      </button>
                        <button 
                        onClick={() => handleAddEventClick(history)}
                        className="bg-white text-2xl w-8 h-8 pb-1 rounded-full z-50 flex items-center justify-center shadow-lg hover:bg-blue-100 hover:border-2 hover:border-blue-500"
                      >
                        +
                      </button>
                      </div>
                    )}
                    {expandedHistoryEvents.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        <h4 className="font-semibold">Events:</h4>
                        {expandedHistoryEvents.map((event) => (
                          <div
                            key={event.id}
                            className="border border-gray-200 bg-gray-50 rounded-lg p-3 flex justify-between items-center relative"
                          >
                            <span>{event.name}</span>
                            {isExpandedHistoryOwner && (
                              <button
                                onClick={() => handleDeleteEventClick(event)}
                                className="bg-white w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:bg-red-100 hover:border-2 hover:border-red-500"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-400">
                        No events.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HistoryList;